import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useSociete } from "../contexts/SocieteContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { DateField } from "./DateField";
import {
  Building2, Plus, Loader2, Save, Trash2, Pencil, Star, CheckCircle2, X,
  MapPin, Phone, Mail, Hash, Briefcase, User, Calendar, Palette
} from "lucide-react";

const COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#06b6d4", "#84cc16", "#f97316", "#6366f1"];

const FORMES = ["SAS", "SARL", "EURL", "SA", "SCI", "SASU", "SNC", "Auto-entrepreneur", "Autre"];

export function SocietesSettings() {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const { reloadSocietes } = useSociete();

  const [societes, setSocietes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editSoc, setEditSoc] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "", siren: "", siret: "", forme_juridique: "", capital: 0,
    adresse: "", ville: "", code_postal: "", pays: "France",
    email: "", telephone: "", tva_intra: "", activite: "",
    dirigeant: "", date_creation_societe: "", color: "#3b82f6",
  });

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/societes");
      setSocietes(r.data || []);
    } catch { setSocietes([]); }
    setLoading(false);
  }, []);

  useEffect(() => { reload(); }, []);

  const openNew = () => {
    setEditSoc(null);
    setForm({
      name: "", siren: "", siret: "", forme_juridique: "", capital: 0,
      adresse: "", ville: "", code_postal: "", pays: "France",
      email: "", telephone: "", tva_intra: "", activite: "",
      dirigeant: "", date_creation_societe: "", color: COLORS[societes.length % COLORS.length],
    });
    setShowDialog(true);
  };

  const openEdit = (s) => {
    setEditSoc(s);
    setForm({
      name: s.name || "", siren: s.siren || "", siret: s.siret || "",
      forme_juridique: s.forme_juridique || "", capital: s.capital || 0,
      adresse: s.adresse || "", ville: s.ville || "", code_postal: s.code_postal || "",
      pays: s.pays || "France", email: s.email || "", telephone: s.telephone || "",
      tva_intra: s.tva_intra || "", activite: s.activite || "",
      dirigeant: s.dirigeant || "", date_creation_societe: s.date_creation_societe || "",
      color: s.color || "#3b82f6",
    });
    setShowDialog(true);
  };

  const save = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (editSoc) {
        await api.put(`/societes/${editSoc.societe_id}`, form);
      } else {
        await api.post("/societes", form);
      }
      setShowDialog(false);
      reload();
      reloadSocietes();
    } catch (e) { alert(e.response?.data?.detail || "Erreur"); }
    setSaving(false);
  };

  const deleteSoc = async (sid) => {
    if (!window.confirm("Supprimer cette société ? Les données liées ne seront pas supprimées.")) return;
    try {
      await api.delete(`/societes/${sid}`);
      reload();
      reloadSocietes();
    } catch (e) { alert(e.response?.data?.detail || "Erreur"); }
  };

  const setDefault = async (sid) => {
    await api.put(`/societes/${sid}/set-default`);
    reload();
    reloadSocietes();
  };

  const F = (key, val) => setForm(f => ({ ...f, [key]: val }));

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#f7c935]" /></div>;

  return (
    <div data-testid="societes-settings">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-bold text-white flex items-center gap-2">
            <Building2 className="w-4 h-4 text-[#f7c935]" /> Sociétés
          </h3>
          <p className="text-[10px] text-slate-500 mt-0.5">{societes.length} société(s) enregistrée(s)</p>
        </div>
        <Button onClick={openNew} size="sm" className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold text-xs" data-testid="add-societe-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> Société
        </Button>
      </div>

      {/* List */}
      {societes.length === 0 ? (
        <div className="text-center py-10 border border-dashed border-white/10 rounded-xl">
          <Building2 className="w-12 h-12 mx-auto mb-3 text-slate-600" />
          <p className="text-sm text-slate-400">Aucune société</p>
          <p className="text-[10px] text-slate-500 mt-1">Créez votre première société pour isoler vos données</p>
        </div>
      ) : (
        <div className="space-y-2">
          {societes.map(s => (
            <div key={s.societe_id} className="bg-[#0b0811] border border-white/5 rounded-xl p-4 hover:border-white/10 transition-colors" data-testid={`societe-card-${s.societe_id}`}>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${s.color || "#3b82f6"}15` }}>
                  <Building2 className="w-5 h-5" style={{ color: s.color || "#3b82f6" }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-white">{s.name}</span>
                    {s.is_default && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#f7c935]/10 text-[#f7c935] font-bold flex items-center gap-0.5">
                        <Star className="w-2.5 h-2.5" /> Par defaut
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
                    {s.forme_juridique && <span className="text-[10px] text-slate-500 flex items-center gap-1"><Briefcase className="w-2.5 h-2.5" /> {s.forme_juridique}</span>}
                    {s.siren && <span className="text-[10px] text-slate-500 flex items-center gap-1"><Hash className="w-2.5 h-2.5" /> SIREN {s.siren}</span>}
                    {s.siret && <span className="text-[10px] text-slate-500 flex items-center gap-1"><Hash className="w-2.5 h-2.5" /> SIRET {s.siret}</span>}
                    {s.ville && <span className="text-[10px] text-slate-500 flex items-center gap-1"><MapPin className="w-2.5 h-2.5" /> {s.ville}</span>}
                    {s.dirigeant && <span className="text-[10px] text-slate-500 flex items-center gap-1"><User className="w-2.5 h-2.5" /> {s.dirigeant}</span>}
                    {s.tva_intra && <span className="text-[10px] text-slate-500">TVA {s.tva_intra}</span>}
                    {s.capital > 0 && <span className="text-[10px] text-slate-500">Capital {s.capital.toLocaleString("fr-FR")} EUR</span>}
                  </div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  {!s.is_default && (
                    <button onClick={() => setDefault(s.societe_id)} className="p-1.5 hover:bg-[#f7c935]/10 rounded-lg" title="Définir par défaut" data-testid={`set-default-${s.societe_id}`}>
                      <Star className="w-3.5 h-3.5 text-slate-500 hover:text-[#f7c935]" />
                    </button>
                  )}
                  <button onClick={() => openEdit(s)} className="p-1.5 hover:bg-white/5 rounded-lg" data-testid={`edit-societe-${s.societe_id}`}>
                    <Pencil className="w-3.5 h-3.5 text-slate-400" />
                  </button>
                  {societes.length > 1 && (
                    <button onClick={() => deleteSoc(s.societe_id)} className="p-1.5 hover:bg-red-500/10 rounded-lg" data-testid={`delete-societe-${s.societe_id}`}>
                      <Trash2 className="w-3.5 h-3.5 text-red-400" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Dialog */}
      <Dialog open={showDialog} onOpenChange={v => { setShowDialog(v); if (!v) setEditSoc(null); }}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editSoc ? "Modifier la société" : "Nouvelle société"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {/* Identité */}
            <div>
              <label className="text-[10px] text-slate-500 uppercase mb-1.5 block font-medium">Identité</label>
              <div className="space-y-2">
                <Input value={form.name} onChange={e => F("name", e.target.value)} placeholder="Nom de la société *" className="bg-[#0b0811] border-white/10 text-white" data-testid="soc-name-input" />
                <div className="grid grid-cols-2 gap-2">
                  <select value={form.forme_juridique} onChange={e => F("forme_juridique", e.target.value)} className="bg-[#0b0811] border border-white/10 text-white rounded-lg px-3 py-2 text-sm">
                    <option value="">Forme juridique</option>
                    {FORMES.map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                  <Input type="number" value={form.capital} onChange={e => F("capital", parseFloat(e.target.value) || 0)} placeholder="Capital social" className="bg-[#0b0811] border-white/10 text-white text-sm" />
                </div>
                <Input value={form.dirigeant} onChange={e => F("dirigeant", e.target.value)} placeholder="Dirigeant / Gérant" className="bg-[#0b0811] border-white/10 text-white text-sm" />
                <Input value={form.activite} onChange={e => F("activite", e.target.value)} placeholder="Activité principale" className="bg-[#0b0811] border-white/10 text-white text-sm" />
                <div>
                  <label className="text-[10px] text-slate-500 mb-1 block">Date de création de la société</label>
                  <DateField value={form.date_creation_societe} onChange={(v) => F("date_creation_societe", v)} testId="socsettings-date-creation" />
                </div>
              </div>
            </div>

            {/* Numéros */}
            <div>
              <label className="text-[10px] text-slate-500 uppercase mb-1.5 block font-medium">Numéros d'identification</label>
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <Input value={form.siren} onChange={e => F("siren", e.target.value)} placeholder="SIREN (9 chiffres)" maxLength={9} className="bg-[#0b0811] border-white/10 text-white text-sm" data-testid="soc-siren-input" />
                  <Input value={form.siret} onChange={e => F("siret", e.target.value)} placeholder="SIRET (14 chiffres)" maxLength={14} className="bg-[#0b0811] border-white/10 text-white text-sm" />
                </div>
                <Input value={form.tva_intra} onChange={e => F("tva_intra", e.target.value)} placeholder="N° TVA Intracommunautaire (ex: FR12345678901)" className="bg-[#0b0811] border-white/10 text-white text-sm" />
              </div>
            </div>

            {/* Coordonnées */}
            <div>
              <label className="text-[10px] text-slate-500 uppercase mb-1.5 block font-medium">Coordonnées</label>
              <div className="space-y-2">
                <Input value={form.adresse} onChange={e => F("adresse", e.target.value)} placeholder="Adresse" className="bg-[#0b0811] border-white/10 text-white text-sm" />
                <div className="grid grid-cols-3 gap-2">
                  <Input value={form.code_postal} onChange={e => F("code_postal", e.target.value)} placeholder="Code postal" className="bg-[#0b0811] border-white/10 text-white text-sm" />
                  <Input value={form.ville} onChange={e => F("ville", e.target.value)} placeholder="Ville" className="bg-[#0b0811] border-white/10 text-white text-sm" />
                  <Input value={form.pays} onChange={e => F("pays", e.target.value)} placeholder="Pays" className="bg-[#0b0811] border-white/10 text-white text-sm" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Input value={form.email} onChange={e => F("email", e.target.value)} placeholder="Email" type="email" className="bg-[#0b0811] border-white/10 text-white text-sm" />
                  <Input value={form.telephone} onChange={e => F("telephone", e.target.value)} placeholder="Téléphone" className="bg-[#0b0811] border-white/10 text-white text-sm" />
                </div>
              </div>
            </div>

            {/* Couleur */}
            <div>
              <label className="text-[10px] text-slate-500 uppercase mb-1.5 block font-medium">Couleur d'identification</label>
              <div className="flex gap-2 flex-wrap">
                {COLORS.map(c => (
                  <button key={c} onClick={() => F("color", c)}
                    className={`w-7 h-7 rounded-lg transition-all ${form.color === c ? "ring-2 ring-white ring-offset-2 ring-offset-[#14101c] scale-110" : "hover:scale-105"}`}
                    style={{ background: c }}
                    data-testid={`color-${c}`} />
                ))}
              </div>
            </div>

            <Button onClick={save} disabled={saving || !form.name.trim()} className="w-full bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold" data-testid="save-societe-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              {editSoc ? "Modifier" : "Créer la société"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
